import { useEffect, useState } from "react";
import { Percent, Save } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail } from "@/lib/api";

export default function AdminSettings() {
  const [form, setForm] = useState({ global_markup_pct: "", b2b_markup_pct: "", air_markup_pct: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    appApi.get("admin/settings").then((d) => {
      setForm({
        global_markup_pct: d.global_markup_pct ?? "",
        b2b_markup_pct: d.b2b_markup_pct ?? "",
        air_markup_pct: d.air_markup_pct ?? "",
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await appApi.patch("admin/settings", {
        global_markup_pct: parseFloat(form.global_markup_pct),
        b2b_markup_pct: parseFloat(form.b2b_markup_pct),
        air_markup_pct: parseFloat(form.air_markup_pct),
      });
      toast.success("Markup settings saved — live across app and web.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { k: "global_markup_pct", label: "Global B2C markup %", hint: "Added on top of every B2C courier rate" },
    { k: "b2b_markup_pct", label: "B2B freight markup %", hint: "Your revenue per B2B shipment" },
    { k: "air_markup_pct", label: "Air markup %", hint: "Blue Dart Air / DTDC Air rates" },
  ];

  return (
    <div data-testid="admin-settings-page" className="max-w-2xl space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Platform Settings</h1>
      </div>

      <form onSubmit={save} className="border-2 border-obsidian bg-white p-6 space-y-5" data-testid="admin-settings-form">
        <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Percent className="w-4 h-4 text-blaze" /> Rate markup
        </p>
        {fields.map((f) => (
          <div key={f.k}>
            <label htmlFor={`setting-${f.k}`} className="text-xs font-mono font-bold uppercase tracking-widest">{f.label}</label>
            <input
              id={`setting-${f.k}`}
              data-testid={`setting-${f.k}-input`}
              type="number"
              min="0"
              step="0.5"
              required
              disabled={loading}
              value={form[f.k]}
              onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
              className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian disabled:opacity-50"
            />
            <p className="text-xs text-zinc-500 mt-1">{f.hint}</p>
          </div>
        ))}
        <button type="submit" disabled={saving || loading} data-testid="admin-settings-save"
          className="inline-flex items-center gap-2 bg-obsidian text-sky-300 font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_rgba(10,10,10,0.35)] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </form>

      <p className="text-xs font-mono text-zinc-400 uppercase tracking-widest">
        Per-merchant overrides live on each merchant's detail page.
      </p>
    </div>
  );
}
